/* ⚠️ GENERATED from app/services/avatarPages.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/avatarPages.ts — what tapping a profile picture actually shows.
//
// 🟥 IT SHOWED NOTHING. The picture at the top of ChatInfoScreen was not
//    touchable at all for a person, for Note to Self, for Allo AI or for the
//    official account; for a group it was touchable only if you were an admin,
//    and it opened an Alert asking whether to CHANGE the photo. The one thing
//    tapping a picture obviously means — show me the picture — was the one thing
//    it could not do.
//
// 🟥 AND A GROUP WITH NO PHOTO IS NOT A GROUP WITH NO PICTURE. Its avatar is a
//    collage of up to four members, so enlarging "the group photo" would enlarge
//    a picture of nobody. What is actually there is several people, and the
//    honest way to show several people is one at a time with a count.
//
// 🟢 SO THE ANSWER IS A LIST OF PAGES, DECIDED HERE. Whether there is one or
//    four, and what each one is, is a property of the conversation — not of the
//    viewer that draws it, and not of a component that happens to be on screen.
//    Both clients ask this the same way, so "1 of 4" counts the same four people
//    in the phone and in the browser.

import { activeMembers, memberPhoto, memberName, webSafePhoto } from './groupMembers';
import { hasAvatar } from './avatar3d';
import { vouchedKind, AI_UID } from './vouchedAccounts';

/** A glyph is drawn, not fetched — the assistant and Note to Self have no photo. */

/**
 * The pages a tap on this avatar should show, in order.
 *
 * 🟢 NEVER EMPTY WHEN THERE IS SOMEBODY TO SHOW. A person with no photo still
 *    gets a page — their initial, large — because a tap that opens nothing is
 *    indistinguishable from a tap that did not register.
 *
 * 🟥 AND A GROUP WITH NO PHOTO AND NO MEMBERS GETS NONE. That is the one case
 *    where there genuinely is nothing behind the picture, and opening an empty
 *    black screen over it would be worse than not opening.
 */
export function avatarPages(subject, opts = {}) {
  if (!subject) return [];
  if (subject.isGroup) {
    const own = webSafePhoto(subject.groupImage);
    /* the group's own photo is the group's picture; there is nothing to page */
    if (own) return [{
      key: 'group',
      uri: own,
      glyph: null,
      name: String(subject.name || '').trim()
    }];

    /* 🟢 THE SAME MEMBERS THE COLLAGE DRAWS, IN THE SAME ORDER — including me,
       and not including anyone who has left. If the counter and the tiles
       disagreed about who is in the group, "1 of 4" would be a lie about a
       picture the reader is looking at. */
    return activeMembers(subject).map(uid => ({
      key: uid,
      uri: memberPhoto(subject, uid, opts),
      glyph: null,
      /* 🟢 the chat's own cache first; then what the screen fetched about
         them; me by my own name — never anybody else's */
      /* 🟥 THE CHAT'S participantNames IS A CACHE WRITTEN BY WHOEVER LAST WROTE THE
         DOCUMENT, and it has been seen carrying one name for several members. The
         member's OWN profile, when the screen has it, is the name; the cache is
         the fallback; and I am always called by my own name. */
      name: (uid === String(opts.myUid || '') ? String(opts.myName || '') : String(opts.names?.[uid] || '')) || memberName(subject, uid, '')
    }));
  }
  const uid = String(subject.uid || '').trim();
  const kind = vouchedKind(uid, opts.myUid);

  /* 🟢 THE ASSISTANT AND NOTE TO SELF HAVE NO PHOTOGRAPH, and that is not a
     missing photo — it is what they look like. The viewer draws the mark it
     already draws small, large. */
  if (kind === 'self') return [{
    key: 'self',
    uri: webSafePhoto(opts.myPhoto),
    glyph: 'note',
    name: String(opts.myName || '')
  }];
  if (uid === AI_UID) return [{
    key: 'ai',
    uri: null,
    glyph: 'ai',
    name: 'Allo AI'
  }];
  const name = String(subject.fullName || subject.name || subject.username || '').trim();
  return [{
    key: uid || 'peer',
    uri: webSafePhoto(subject.profileImage),
    glyph: null,
    name,
    avatar: hasAvatar(subject) ? subject.avatar : null
  }];
}

/** "2 / 4" — shown only when there is more than one page to be on. */
export function pageLabel(index, total) {
  if (!total || total < 2) return '';
  const i = Math.max(0, Math.min(index, total - 1));
  return `${i + 1} / ${total}`;
}
